/* eslint-disable no-unused-vars */
import { get, set, uniq, cloneDeep } from 'lodash';
import { CARE_JOURNAL, CARE_JOURNAL_WHITEBOARD } from 'routes/constants';
import { DIGITAL_SHARED_DISPLAY_ROLE } from './constants';

/**
 * Check if the user role is the Digital Shared Display Role.
 * @param {Object} userRole - The user role object.
 * @returns {boolean} - True if the user role is the Digital Shared Display Role, false otherwise.
 */
export const isDigitalSharedDisplayRole = userRole => {
  return get(userRole, 'name') === DIGITAL_SHARED_DISPLAY_ROLE;
};

/**
 * Get levelIDs based on the user's role for Split
 * @param {*} role
 * @param {*} levels
 * @param {*} isMFS
 * @returns
 */
export const extractLevelIdsByRoleForSplit = (
  role = '',
  levels = [],
  isMFS = true
) => {
  if (!role) return [];

  const ACCESS_ALLOWED = ['all'];
  const ACCESS_DENIED = ['cj_access_denied'];

  const restrictedCJAccessRoles = [
    'assistant_teacher',
    'Staff',
    'css',
    'Education Development Specialist',
    'Childhood Development Specialist',
    'reader',
    'enrolment_advisor',
    'bc_user',
  ];

  const allowedCJAccessRoles = [
    'senior_teacher',
    'default_principal',
    'centre_support',
    'mfs_hq',
    'tech_super_admin',
    'default_hq',
    'Support Role',
    'L1 Support Lead Role',
  ];

  if (restrictedCJAccessRoles.includes(role)) return ACCESS_DENIED;
  if (allowedCJAccessRoles.includes(role)) return ACCESS_ALLOWED;
  if (role === 'default_teacher' && get(levels, 'length') > 0)
    return uniq(levels?.map(lv => lv?.id?.toString()));
  if (role === 'admin') return isMFS ? ACCESS_DENIED : ACCESS_ALLOWED;
  if (role === 'hq_bu_support') return isMFS ? ACCESS_ALLOWED : ACCESS_DENIED;

  return [];
};

/**
 * Check and extract to show / hide the CJ menus based on the treatment
 * @param {*} sideMenus menu object array
 * @returns array of menus
 */
export const extractSideMenuForCJ = (
  user = {},
  sideMenus = [],
  isMFS = true,
  userClassLevels = []
) => {
  const menus = cloneDeep(sideMenus);
  const role = get(user, 'userRoleRelations.data[0].role.name', '');
  let centreIds = [];
  const centres = uniq(
    (get(user, 'userAccessControls.data') || [])
      .map(c => get(c, 'centre.ID'))
      .filter(id => id)
  );
  centreIds = centres.map(d => d.toString());
  if (centres.length === 0) centreIds = ['all'];

  const removeCJMenu = () => {
    const cJMenuMatcher = menu => {
      return [CARE_JOURNAL, CARE_JOURNAL_WHITEBOARD].includes(`/${menu?.path}`);
    };

    const hasCJMenuIndex = menus.findIndex(m =>
      (m?.submenu || []).some(cJMenuMatcher)
    );
    if (hasCJMenuIndex > -1) {
      set(
        menus,
        `[${hasCJMenuIndex}].submenu`,
        (menus?.[hasCJMenuIndex]?.submenu || [])?.filter(s => !cJMenuMatcher(s))
      );
    }
  };

  const levelIds = extractLevelIdsByRoleForSplit(role, userClassLevels, isMFS);

  if (isMFS) {
    if (
      !(
        levelIds.some(lvl => ['all', '2'].includes(lvl)) &&
        centreIds.some(centre =>
          [
            'all',
            '11',
            '147',
            '158',
            '28',
            '92',
            '4',
            '134',
            '27',
            '6',
            '83',
            '161',
            '118',
            '146',
            '94',
            '40',
            '164',
            '89',
            '121',
            '165',
            '65',
            '35',
            '38',
            '107',
            '123',
            '49',
            '150',
            '153',
            '114',
            '31',
            '22',
            '52',
            '93',
            '151',
            '97',
            '88',
            '73',
            '36',
            '142',
            '85',
            '16',
            '162',
            '160',
            // 2B release
            '10',
            '69',
            '127',
            '21',
            '99',
            '132',
            '24',
            '46',
            '86',
            '51',
            '117',
            '30',
            '14',
            '96',
            '37',
            '62',
            '32',
            '131',
            '67',
            '26',
            '58',
            '50',
            // 2C release
            '163',
            '115',
            '157',
            '64',
            '81',
            '25',
            '98',
            '41',
            '48',
            '12',
            '72',
            '9',
            '141',
            '17',
            '159',
            // 2D release
            '63',
            '100',
            '19',
            '43',
            '133',
            '116',
            '143',
            '126',
            '42',
            '77',
            '60',
            '15',
            '128',
            '108',
            '3',
            // 2E release
            '78',
            '135',
            '124',
            '122',
            '61',
            '5',
            '91',
            '70',
            '44',
            '140',
            '112',
            '111',
            '137',
            '90',
            '74',
            // 2F release
            '145',
            '76',
            '113',
            '119',
            '33',
            '13',
            '103',
            '152',
            '87',
            '79',
            '2',
            // 2G release
            '148',
            '101',
            '34',
            '129',
            '104',
            '47',
            // 2H release
            '136',
            '54',
            '68',
            '8',
            '59',
            // 2I release
            '23',
            '95',
            '149',
            // New centre
            '166',
            // New centre
            '167',
            // New centre - Tengah 02
            '168',
            // New centre - Woodlands 10
            '169',
            // New centre - Bukit Batok 05
            '170',
            // New centre - Tamnpines 7
            '171',
          ].includes(centre)
        )
      )
    ) {
      removeCJMenu();
    }
  } else if (
    !(
      levelIds.some(lvl => ['10', '11', '12', 'all'].includes(lvl)) &&
      centreIds.some(centre =>
        [
          'all',
          '4',
          '2',
          '3',
          '6',
          '7',
          '8',
          '9',
          '11',
          '12',
          '13',
          '14',
          '15',
          '17',
          '19',
        ].includes(centre)
      )
    )
  ) {
    removeCJMenu();
  }
  return menus;
};
